import { useEffect, useMemo, useRef, useState } from "react";
import {
  HeartPulse,
  Camera,
  Clock,
  CheckCircle2,
  Copy,
  Check,
  Loader2,
  AlertCircle,
  X,
  Smartphone,
  Heart,
  Image as ImageIcon,
} from "lucide-react";
import { BpUploadConfig } from "../../types/landingPage";
import { track } from "../../utils/rudderstack";

const DEFAULT_MAX_IMAGES = 5;
const MAX_BYTES_PER_IMAGE = 10 * 1024 * 1024;

interface SelectedImage {
  id: string;
  file: File;
  previewUrl: string;
}

type SubmitState =
  | { status: "idle" }
  | { status: "uploading" }
  | { status: "success"; uploadedCount: number }
  | { status: "error"; message: string };

interface BPUploadSectionProps {
  /** Identifies the study/landing page so analytics + the deep-link route resolve correctly. */
  landingPageId: string;
  /** UI copy for this study. */
  bpUpload: BpUploadConfig;
  /**
   * When provided, the section will create or fetch a token via
   * bp-upload-create-token using the just-submitted patient details. This is
   * the thank-you page flow.
   */
  patient?: {
    patientId: number;
    applicationSiteId: number | null;
  };
  /**
   * When provided, the section is operating in deep-link mode — the token has
   * already been resolved by the standalone /bp-upload/:token page.
   * Used to preserve the count of previously uploaded readings.
   *
   * NOTE: only a count is ever surfaced — never image URLs or filenames.
   */
  resolvedToken?: {
    token: string;
    alreadyUploadedCount: number;
  };
  /**
   * Hide the "I'll do this later" affordance — used in deep-link mode where
   * the user already followed the deferred path.
   */
  hideLaterCta?: boolean;
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const commaIdx = result.indexOf(",");
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Read failed"));
    reader.readAsDataURL(file);
  });
}

export function BPUploadSection({
  landingPageId,
  bpUpload,
  patient,
  resolvedToken,
  hideLaterCta,
}: BPUploadSectionProps) {
  const maxImages = bpUpload.maxImages ?? DEFAULT_MAX_IMAGES;
  const [token, setToken] = useState<string | null>(
    resolvedToken?.token ?? null,
  );
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [images, setImages] = useState<SelectedImage[]>([]);
  const [submitState, setSubmitState] = useState<SubmitState>({ status: "idle" });
  const [showLaterPanel, setShowLaterPanel] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const viewedRef = useRef(false);

  // Fetch the token in thank-you-page mode (post-submission). Skipped when
  // resolvedToken is already provided (deep-link mode).
  useEffect(() => {
    if (resolvedToken || !patient) return;
    let cancelled = false;
    async function createToken() {
      if (!patient) return;
      try {
        const response = await fetch("/.netlify/functions/bp-upload-create-token", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            patientId: patient.patientId,
            applicationSiteId: patient.applicationSiteId,
            landingPageId,
          }),
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const json = (await response.json()) as { token: string };
        if (!cancelled) setToken(json.token);
      } catch (error) {
        console.error("Failed to create BP upload token", error);
        if (!cancelled) setTokenError("We couldn't set up the upload right now.");
      }
    }
    createToken();
    return () => {
      cancelled = true;
    };
  }, [patient, landingPageId, resolvedToken]);

  // Fire viewed event once when the section is on screen.
  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    track("lp.bp_upload.section_viewed", {
      landing_page_id: landingPageId,
      mode: resolvedToken ? "deep_link" : "thank_you",
    });
  }, [landingPageId, resolvedToken]);

  // Revoke object URLs on unmount.
  useEffect(() => {
    return () => {
      images.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    };
  }, [images]);

  function handleFiles(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    setPickerError(null);

    const accepted: SelectedImage[] = [];
    const remaining = maxImages - images.length;
    let rejectedReason: string | null = null;

    for (const file of Array.from(fileList)) {
      if (accepted.length >= remaining) {
        rejectedReason = `You can share up to ${maxImages} images at a time.`;
        break;
      }
      if (!file.type.startsWith("image/")) {
        rejectedReason = "Only image files are supported.";
        continue;
      }
      if (file.size > MAX_BYTES_PER_IMAGE) {
        rejectedReason = "Each image must be under 10 MB.";
        continue;
      }
      accepted.push({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()
          .toString(36)
          .slice(2)}`,
        file,
        previewUrl: URL.createObjectURL(file),
      });
    }

    if (rejectedReason) setPickerError(rejectedReason);

    if (accepted.length > 0) {
      setImages((prev) => [...prev, ...accepted]);
      track("lp.bp_upload.images_selected", {
        landing_page_id: landingPageId,
        count: accepted.length,
        total: images.length + accepted.length,
      });
    }
  }

  function removeImage(id: string) {
    setImages((prev) => {
      const removed = prev.find((img) => img.id === id);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((img) => img.id !== id);
    });
  }

  async function handleSubmit() {
    if (!token || images.length === 0 || submitState.status === "uploading") return;
    setSubmitState({ status: "uploading" });

    try {
      const payload = await Promise.all(
        images.map(async (img) => ({
          filename: img.file.name,
          mimeType: img.file.type,
          data: await readFileAsBase64(img.file),
        })),
      );

      const response = await fetch("/.netlify/functions/bp-upload-submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, images: payload }),
      });

      if (!response.ok) {
        const errBody = (await response
          .json()
          .catch(() => ({}))) as { error?: string };
        throw new Error(errBody.error || `Upload failed (${response.status})`);
      }

      const json = (await response.json()) as { uploadedCount: number };
      setSubmitState({ status: "success", uploadedCount: json.uploadedCount });
      track("lp.bp_upload.upload_succeeded", {
        landing_page_id: landingPageId,
        count: json.uploadedCount,
      });
      images.forEach((img) => URL.revokeObjectURL(img.previewUrl));
      setImages([]);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Upload failed.";
      setSubmitState({ status: "error", message });
      track("lp.bp_upload.upload_failed", {
        landing_page_id: landingPageId,
        error: message,
      });
    }
  }

  function handleShowLater() {
    setShowLaterPanel(true);
    track("lp.bp_upload.skipped", {
      landing_page_id: landingPageId,
      reason: "deferred",
    });
    if (token) {
      track("lp.bp_upload.shareable_link_generated", {
        landing_page_id: landingPageId,
        token,
      });
    }
  }

  const shareUrl = useMemo(() => {
    if (!token) return null;
    if (typeof window === "undefined") return null;
    return `${window.location.origin}/bp-upload/${token}`;
  }, [token]);

  async function copyShareLink() {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      track("lp.bp_upload.shareable_link_copied", {
        landing_page_id: landingPageId,
        token,
      });
      setTimeout(() => setLinkCopied(false), 2500);
    } catch {
      // Fallback: select the input contents
      const input = sectionRef.current?.querySelector(
        "input[data-share-url]",
      ) as HTMLInputElement | null;
      input?.select();
    }
  }

  if (submitState.status === "success") {
    return (
      <SuccessCard
        headline={bpUpload.successHeadline}
        body={bpUpload.successBody}
        uploadedCount={submitState.uploadedCount}
      />
    );
  }

  return (
    <div
      ref={sectionRef}
      className="w-full max-w-2xl mx-auto px-2 mt-6"
      data-testid="bp-upload-section"
    >
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-7 pb-2 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary-50 text-primary-500 mb-4 floating-orb-pulse">
            <HeartPulse className="w-7 h-7" strokeWidth={2} />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-primary-500 to-primary-600">
            {bpUpload.headline}
          </h2>
          <p className="mt-3 text-base text-gray-600 max-w-lg mx-auto">
            {bpUpload.bodyText}
          </p>

          {resolvedToken && resolvedToken.alreadyUploadedCount > 0 && (
            <p
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 bg-primary-50 px-3 py-1 rounded-full"
              data-testid="bp-upload-already-shared"
            >
              <CheckCircle2 className="w-4 h-4" />
              You've shared {resolvedToken.alreadyUploadedCount}{" "}
              {resolvedToken.alreadyUploadedCount === 1 ? "reading" : "readings"} so far —
              feel free to add more
            </p>
          )}
        </div>

        {/* Three example chips */}
        <div className="px-6 pt-4">
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Chip icon={<Smartphone className="w-3.5 h-3.5" />} label="Doctor's app screenshot" />
            <Chip icon={<Heart className="w-3.5 h-3.5" />} label="Apple Health screenshot" />
            <Chip icon={<Camera className="w-3.5 h-3.5" />} label="Photo of monitor" />
          </div>
        </div>

        {/* Instructions callout */}
        <div className="mx-6 mt-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
          <Clock className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" strokeWidth={2} />
          <div className="text-sm text-amber-900">
            <p className="font-semibold">{bpUpload.instructionTitle}</p>
            <p className="mt-1 text-amber-900/90">{bpUpload.instructionText}</p>
          </div>
        </div>

        {/* Image picker */}
        <div className="px-6 pt-5">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            data-testid="bp-upload-file-input"
            onChange={(event) => {
              handleFiles(event.target.files);
              event.target.value = "";
            }}
          />

          {images.length === 0 ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={!token || submitState.status === "uploading"}
              className="w-full rounded-xl border-2 border-dashed border-primary-200 bg-primary-50/40 hover:bg-primary-50 transition-colors px-4 py-8 text-center disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="bp-upload-add-images"
            >
              <ImageIcon className="w-8 h-8 mx-auto text-primary-500" strokeWidth={2} />
              <p className="mt-2 text-base font-semibold text-gray-900">
                Add your readings
              </p>
              <p className="text-sm text-gray-500 mt-0.5">
                Tap to take a photo or pick from your library
              </p>
            </button>
          ) : (
            <div>
              <div className="grid grid-cols-3 gap-3">
                {images.map((img) => (
                  <div
                    key={img.id}
                    className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200 shadow-sm"
                  >
                    <img
                      src={img.previewUrl}
                      alt="Selected reading preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      aria-label="Remove image"
                      onClick={() => removeImage(img.id)}
                      className="absolute top-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-black/70 text-white hover:bg-black"
                    >
                      <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                    </button>
                  </div>
                ))}
                {images.length < maxImages && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="aspect-square rounded-lg border-2 border-dashed border-primary-200 bg-primary-50/40 hover:bg-primary-50 flex flex-col items-center justify-center text-primary-600"
                    data-testid="bp-upload-add-more"
                  >
                    <Camera className="w-5 h-5" strokeWidth={2} />
                    <span className="mt-1 text-xs font-semibold">Add more</span>
                  </button>
                )}
              </div>
              <p className="mt-2 text-xs text-gray-500 text-right">
                {images.length} of {maxImages}
              </p>
            </div>
          )}

          {pickerError && (
            <p className="mt-2 text-sm text-amber-700 inline-flex items-center gap-1.5">
              <AlertCircle className="w-4 h-4" /> {pickerError}
            </p>
          )}
          {tokenError && !token && (
            <p className="mt-2 text-sm text-red-600 inline-flex items-center gap-1.5">
              <AlertCircle className="w-4 h-4" /> {tokenError}
            </p>
          )}
        </div>

        {/* CTAs */}
        <div className="px-6 pt-5 pb-6">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={
              !token || images.length === 0 || submitState.status === "uploading"
            }
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-base font-semibold py-3 transition-colors"
            data-testid="bp-upload-submit"
          >
            {submitState.status === "uploading" ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Sending…
              </>
            ) : (
              <>{bpUpload.ctaLabel}</>
            )}
          </button>

          {!hideLaterCta && !showLaterPanel && (
            <button
              type="button"
              onClick={handleShowLater}
              className="mt-2 w-full text-sm text-gray-500 hover:text-gray-700 py-2"
              data-testid="bp-upload-later"
            >
              {bpUpload.laterCtaLabel}
            </button>
          )}

          {submitState.status === "error" && (
            <p className="mt-3 text-sm text-red-600 inline-flex items-start gap-1.5">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Something went wrong: {submitState.message}. Please try again.
              </span>
            </p>
          )}

          {showLaterPanel && shareUrl && (
            <div className="mt-4 rounded-xl bg-gray-50 border border-gray-200 px-4 py-4">
              <p className="text-sm text-gray-700 font-medium">
                We'll text you a reminder. You can also save this private link to come
                back any time:
              </p>
              <div className="mt-2 flex items-stretch gap-2">
                <input
                  data-share-url
                  type="text"
                  readOnly
                  value={shareUrl}
                  className="flex-1 min-w-0 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <button
                  type="button"
                  onClick={copyShareLink}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 px-3 text-sm font-semibold text-white"
                  data-testid="bp-upload-copy-link"
                >
                  {linkCopied ? (
                    <>
                      <Check className="w-4 h-4" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" /> Copy
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Reassurance */}
        <div className="px-6 pb-6 pt-1 text-center">
          <p className="text-xs text-gray-400">
            Your application is already submitted — this just helps us match you to the
            right study faster. Only the study team will see these. You can stop any
            time.
          </p>
        </div>
      </div>
    </div>
  );
}

function Chip({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 text-primary-700 px-3 py-1 text-xs font-medium">
      {icon}
      {label}
    </span>
  );
}

function SuccessCard({
  headline,
  body,
  uploadedCount,
}: {
  headline: string;
  body: string;
  uploadedCount: number;
}) {
  return (
    <div className="w-full max-w-2xl mx-auto px-2 mt-6" data-testid="bp-upload-success">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden p-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 text-green-600 mb-5">
          <CheckCircle2 className="w-9 h-9" strokeWidth={2} />
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{headline}</h2>
        <p className="mt-3 text-base text-gray-600 max-w-md mx-auto">{body}</p>
        <p className="mt-3 text-sm text-gray-400">
          {uploadedCount} {uploadedCount === 1 ? "image" : "images"} received.
        </p>
      </div>
    </div>
  );
}
